/**
 * One deal's files as a table (Files screen 2): what each file is, the period
 * it covers, where it sits, and whether Doppler has finished reading it.
 */
import { Download, Trash2 } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { documentTypeLabel } from "../../config/documentTypes";
import type { LibraryFile } from "../../hooks/useFileLibrary";
import { isFileBeingRead } from "../AddFiles/DealFileStatus";
import { formatPeriod } from "../../utils/filePeriod";
import { formatDate, formatFileSize } from "../../utils/formatUtils";
import { filePeople } from "../../utils/folderSharing";
import PeopleBubbles from "./PeopleBubbles";

interface DealFilesTableProps {
	files: LibraryFile[];
	/** Asks to delete one file; omitted, rows offer no delete. */
	onDelete?: (file: LibraryFile) => void;
	/** Saves one file under its uploaded name; omitted, rows offer none. */
	onDownload?: (file: LibraryFile) => void;
	/** An upload into the deal is running: deletes wait for it. */
	deleteBlocked?: boolean;
	/** Show who added each file (Deal Room sharing). */
	showAddedBy?: boolean;
}

const ReadingPill: React.FC<{ file: LibraryFile }> = ({ file }) => {
	switch (file.classification_status) {
		case "pending":
		case "processing":
			return (
				<span className="inline-flex h-[22px] items-center rounded-full bg-[#fffaeb] px-2 text-[11.5px] font-semibold text-[#b54708]">
					Reading…
				</span>
			);
		case "failed":
			return (
				<span
					title={file.classification_error ?? undefined}
					className="inline-flex h-[22px] items-center rounded-full bg-[#fef3f2] px-2 text-[11.5px] font-semibold text-[#b42318]"
				>
					Couldn't read
				</span>
			);
		default:
			return (
				<span className="inline-flex h-[22px] items-center rounded-full bg-[#E6F3F3] px-2 text-[11.5px] font-semibold text-[#004C4B]">
					Ready
				</span>
			);
	}
};

/**
 * One column template for the header and every row, so each heading sits
 * exactly over its column. The file name is left-aligned; the other columns,
 * headings and values alike, are centred. Columns drop out at
 * the same breakpoints the cells hide at.
 */
const COLUMNS =
	"grid items-center gap-x-3 px-4 grid-cols-[minmax(0,1fr)_100px_60px] md:grid-cols-[minmax(0,1fr)_170px_110px_100px_60px] lg:grid-cols-[minmax(0,1fr)_170px_110px_150px_100px_60px]";
/** The same, with an "Added by" column from `lg` up. */
const COLUMNS_WITH_ADDED_BY =
	"grid items-center gap-x-3 px-4 grid-cols-[minmax(0,1fr)_100px_60px] md:grid-cols-[minmax(0,1fr)_170px_110px_100px_60px] lg:grid-cols-[minmax(0,1fr)_160px_110px_140px_80px_100px_60px]";

/** Who added the file (and the deal's owner, when someone else): bubbles only,
 * names in the tooltip. */
const AddedBy: React.FC<{ file: LibraryFile }> = ({ file }) => {
	const people = filePeople(file);
	return (
		<span className="hidden justify-center lg:flex">
			{people.length ? (
				<PeopleBubbles people={people} />
			) : (
				<span className="text-gray-400">—</span>
			)}
		</span>
	);
};

const DealFileRow: React.FC<{
	file: LibraryFile;
	onDelete?: (file: LibraryFile) => void;
	onDownload?: (file: LibraryFile) => void;
	deleteBlocked: boolean;
	showAddedBy: boolean;
}> = React.memo(({ file, onDelete, onDownload, deleteBlocked, showAddedBy }) => {
	const period = formatPeriod(
		file.document?.period_start_date,
		file.document?.period_end_date,
		file.document?.document_date,
	);
	return (
		<div
			className={`group relative ${showAddedBy ? COLUMNS_WITH_ADDED_BY : COLUMNS} border-b border-[#f1f2f4] py-2.5 text-[13px] last:border-b-0 hover:bg-[#f6fbfb]`}
		>
			<div className="min-w-0">
				{/* The whole row is the link: its ::after covers the row, and the
				    delete button sits above it. */}
				<Link
					to={`/files/file/${file.id}`}
					className="block truncate text-[13.5px] font-semibold text-gray-900 after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-[#004C4B]"
				>
					{file.filename}
				</Link>
				<div className="mt-0.5 truncate text-xs text-text-muted">
					Added {formatDate(file.created_at)} · {formatFileSize(file.file_size)}
				</div>
			</div>
			<span className="hidden truncate text-center md:block">
				{documentTypeLabel(file.documentType)}
			</span>
			<span className="hidden truncate text-center text-gray-700 md:block">
				{period ?? "—"}
			</span>
			<span className="hidden truncate text-center text-gray-700 lg:block">
				{file.folderPath.length ? file.folderPath.join(" › ") : "Deal folder"}
			</span>
			{showAddedBy && <AddedBy file={file} />}
			<span className="text-center">
				<ReadingPill file={file} />
			</span>
			<span className="flex justify-end gap-0.5">
				{onDownload && (
					<button
						type="button"
						aria-label={`Download ${file.filename}`}
						title="Download"
						onClick={() => onDownload(file)}
						className="relative z-10 flex h-7 w-7 items-center justify-center rounded text-gray-400 opacity-0 hover:bg-gray-100 hover:text-gray-700 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004C4B] group-hover:opacity-100"
					>
						<Download className="h-3.5 w-3.5" aria-hidden />
					</button>
				)}
				{/* No delete while Doppler is still reading the file: it only
				    becomes deletable once it has settled (ready or unreadable). */}
				{onDelete && !isFileBeingRead(file) && (
					<button
						type="button"
						aria-label={`Delete ${file.filename}`}
						title={
							deleteBlocked
								? "Files are still being added to this deal. You can delete once that finishes."
								: "Delete file"
						}
						disabled={deleteBlocked}
						onClick={() => onDelete(file)}
						className="relative z-10 flex h-7 w-7 items-center justify-center rounded text-gray-400 opacity-0 hover:bg-red-50 hover:text-red-600 focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#004C4B] group-hover:opacity-100 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent"
					>
						<Trash2 className="h-3.5 w-3.5" aria-hidden />
					</button>
				)}
			</span>
		</div>
	);
});
DealFileRow.displayName = "DealFileRow";

const DealFilesTable: React.FC<DealFilesTableProps> = ({
	files,
	onDelete,
	onDownload,
	deleteBlocked = false,
	showAddedBy = false,
}) => (
	<div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
		<div
			className={`${showAddedBy ? COLUMNS_WITH_ADDED_BY : COLUMNS} border-b border-gray-200 bg-[#fafafa] py-2 text-xs font-semibold text-[#5B6472]`}
		>
			<span>File</span>
			<span className="hidden text-center md:block">What it is</span>
			<span className="hidden text-center md:block">Period</span>
			<span className="hidden text-center lg:block">Folder</span>
			{showAddedBy && <span className="hidden text-center lg:block">Added by</span>}
			<span className="text-center">Status</span>
			<span />
		</div>
		{files.map((file) => (
			<DealFileRow
				key={file.id}
				file={file}
				onDelete={onDelete}
				onDownload={onDownload}
				deleteBlocked={deleteBlocked}
				showAddedBy={showAddedBy}
			/>
		))}
	</div>
);

export default DealFilesTable;
